import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { MdCompareArrows } from "react-icons/md";
import { Check, Close } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  icon: {
    color: "#9c27b0",
    width: 24,
    height: 24,
  },
  content: {
    padding: theme.spacing(3),
  },
  previewBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
  outputPreview: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: theme.spacing(2),
  },
  outputItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
  },
  trueOutput: {
    backgroundColor: "rgba(76, 175, 80, 0.1)",
    color: "#4caf50",
  },
  falseOutput: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    color: "#f44336",
  },
  helperText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

const operators = [
  { value: "equals", label: "Igual a (=)", description: "Verifica se os valores são iguais" },
  { value: "notEquals", label: "Diferente de (!=)", description: "Verifica se os valores são diferentes" },
  { value: "contains", label: "Contém", description: "Verifica se o texto contém o valor" },
  { value: "greaterThan", label: "Maior que (>)", description: "Verifica se o primeiro valor é maior" },
  { value: "lessThan", label: "Menor que (<)", description: "Verifica se o primeiro valor é menor" },
  { value: "greaterOrEqual", label: "Maior ou igual (>=)", description: "Verifica se é maior ou igual" },
  { value: "lessOrEqual", label: "Menor ou igual (<=)", description: "Verifica se é menor ou igual" },
  { value: "startsWith", label: "Começa com", description: "Verifica se o texto começa com o valor" },
  { value: "endsWith", label: "Termina com", description: "Verifica se o texto termina com o valor" },
  { value: "isEmpty", label: "Está vazio", description: "Verifica se o valor está vazio" },
  { value: "isNotEmpty", label: "Não está vazio", description: "Verifica se o valor não está vazio" },
  { value: "isTrue", label: "É verdadeiro", description: "Verifica se o valor é true/1/sim" },
  { value: "isFalse", label: "É falso", description: "Verifica se o valor é false/0/não" },
];

const valueTypes = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Número" },
  { value: "boolean", label: "Booleano" },
];

// Filtra operadores baseado no tipo de valor
const getOperatorsForType = (valueType) => {
  if (valueType === "boolean") {
    return operators.filter(op =>
      ["isTrue", "isFalse", "equals", "notEquals"].includes(op.value)
    );
  }
  if (valueType === "number") {
    return operators.filter(op =>
      !["contains", "startsWith", "endsWith", "isTrue", "isFalse"].includes(op.value)
    );
  }
  // text - todos exceto isTrue/isFalse
  return operators.filter(op =>
    !["isTrue", "isFalse"].includes(op.value)
  );
};

const FlowBuilderAddCaseModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    leftValue: "",
    operator: "equals",
    rightValue: "",
    valueType: "text",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open === "edit" && data?.data) {
      setFormData({
        leftValue: data.data.leftValue || "",
        operator: data.data.operator || "equals",
        rightValue: data.data.rightValue || "",
        valueType: data.data.valueType || "text",
      });
    } else {
      setFormData({
        leftValue: "",
        operator: "equals",
        rightValue: "",
        valueType: "text",
      });
    }
  }, [data, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };

      // Auto-ajustar operador quando muda tipo
      if (name === "valueType") {
        if (value === "boolean") {
          newData.operator = "isTrue";
          newData.rightValue = "";
        } else if (prev.operator === "isTrue" || prev.operator === "isFalse") {
          newData.operator = "equals";
        }
      }

      return newData;
    });
  };

  const handleSubmit = () => {
    setLoading(true);
    try {
      const noValueOperators = ["isEmpty", "isNotEmpty", "isTrue", "isFalse"];
      const nodeData = {
        leftValue: formData.leftValue,
        operator: formData.operator,
        rightValue: noValueOperators.includes(formData.operator) ? "" : formData.rightValue,
        valueType: formData.valueType,
      };

      if (open === "edit" && data) {
        onUpdate({
          ...data,
          data: {
            ...data.data,
            ...nodeData,
          },
        });
      } else {
        onSave(nodeData);
      }
    } catch (error) {
      console.error("Erro ao salvar condição:", error);
    } finally {
      setLoading(false);
      close(null);
    }
  };

  const handleClose = () => {
    close(null);
  };

  const getOperatorSymbol = (op) => {
    const symbols = {
      equals: "=",
      notEquals: "!=",
      contains: "contém",
      greaterThan: ">",
      lessThan: "<",
      greaterOrEqual: ">=",
      lessOrEqual: "<=",
      startsWith: "começa com",
      endsWith: "termina com",
      isEmpty: "está vazio",
      isNotEmpty: "não está vazio",
      isTrue: "é verdadeiro",
      isFalse: "é falso",
    };
    return symbols[op] || op;
  };

  const noValueOperators = ["isEmpty", "isNotEmpty", "isTrue", "isFalse"];
  const showRightValue = !noValueOperators.includes(formData.operator);
  const availableOperators = getOperatorsForType(formData.valueType);
  const selectedOperator = operators.find((op) => op.value === formData.operator);

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <MdCompareArrows className={classes.icon} />
          <Typography variant="h6">
            {open === "edit" ? "Editar Condição" : "Nova Condição"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.content} dividers>
        <Grid container spacing={3}>
          {/* Tipo de Valor */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Tipo de valor
            </Typography>
            <TextField
              select
              fullWidth
              name="valueType"
              value={formData.valueType}
              onChange={handleChange}
              variant="outlined"
              size="small"
            >
              {valueTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography className={classes.helperText}>
              Selecione o tipo de dado que será comparado
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Valor a ser comparado
            </Typography>
            <TextField
              fullWidth
              placeholder="Ex: ${variavel} ou texto fixo"
              name="leftValue"
              value={formData.leftValue}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
            <Typography className={classes.helperText}>
              Use ${"{variavel}"} para usar variáveis do fluxo. Ex: ${"{isFromMe}"}, ${"{nome}"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Tipo de comparação
            </Typography>
            <TextField
              select
              fullWidth
              name="operator"
              value={formData.operator}
              onChange={handleChange}
              variant="outlined"
              size="small"
            >
              {availableOperators.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {showRightValue && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Comparar com
              </Typography>
              {formData.valueType === "boolean" ? (
                <TextField
                  select
                  fullWidth
                  name="rightValue"
                  value={formData.rightValue}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value="true">Verdadeiro (true)</MenuItem>
                  <MenuItem value="false">Falso (false)</MenuItem>
                </TextField>
              ) : formData.valueType === "number" ? (
                <TextField
                  fullWidth
                  type="number"
                  placeholder="Ex: 10"
                  name="rightValue"
                  value={formData.rightValue}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                />
              ) : (
                <TextField
                  fullWidth
                  placeholder="Ex: ${outra_variavel} ou texto fixo"
                  name="rightValue"
                  value={formData.rightValue}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                />
              )}
              <Typography className={classes.helperText}>
                {formData.valueType === "boolean"
                  ? "Selecione o valor booleano para comparar"
                  : "Use ${\"variavel\"} para usar variáveis do fluxo"}
              </Typography>
            </Grid>
          )}

          {/* Preview */}
          <Grid item xs={12}>
            <Paper className={classes.previewBox} elevation={0}>
              <Typography variant="subtitle2" gutterBottom>
                Prévia da condição:
              </Typography>
              <Box textAlign="center" py={1}>
                <Typography variant="body1">
                  <strong>{formData.leftValue || "valor1"}</strong>{" "}
                  <span style={{ color: "#9c27b0" }}>
                    {getOperatorSymbol(formData.operator)}
                  </span>{" "}
                  {showRightValue && (
                    <strong>{formData.rightValue || "valor2"}</strong>
                  )}
                </Typography>
              </Box>

              <Divider style={{ margin: "16px 0" }} />

              <Typography variant="subtitle2" gutterBottom>
                Saídas do nó:
              </Typography>
              <Box className={classes.outputPreview}>
                <Box className={`${classes.outputItem} ${classes.trueOutput}`}>
                  <Check style={{ width: 18, height: 18 }} />
                  <Typography variant="body2">Verdadeiro</Typography>
                </Box>
                <Box className={`${classes.outputItem} ${classes.falseOutput}`}>
                  <Close style={{ width: 18, height: 18 }} />
                  <Typography variant="body2">Falso</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading || !formData.leftValue}
        >
          {loading ? <CircularProgress size={24} /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderAddCaseModal;
